/**
 * KCIS Admin Console API helper.
 *
 * Uses the SAME Check-In JWT that Dr. C receives on login (issued as an
 * admin token when `role='checkin_admin'`). Namespaced local-storage key
 * `checkin.token` is shared with the consumer API so admins don't need
 * a second login. Every admin route requires the admin JWT server-side.
 */
const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "checkin.token";

function getToken() {
  // Preferred: Check-In admin JWT. Fallback: KCIS super-admin JWT
  // (single-sign-on so KCIS super admins reach /checkin/admin without
  // a second login). Both tokens are HS256 with the same JWT_SECRET.
  try {
    const ci = localStorage.getItem(TOKEN_KEY) || "";
    if (ci) return ci;
    const kcis = localStorage.getItem("kcis_admin_token") || "";
    return kcis || "";
  } catch { return ""; }
}

async function req(path, { method = "GET", body, query } = {}) {
  const token = getToken();
  if (!token) {
    const err = new Error("Not signed in as admin.");
    err.status = 401;
    throw err;
  }
  const qs = query
    ? "?" + Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&")
    : "";
  const r = await fetch(`${API}/api/kcis/admin${path}${qs}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await r.json(); } catch { /* ignore */ }
  if (!r.ok) {
    const detail = data?.detail;
    const msg = (detail && typeof detail === "object" && detail.message)
      || (detail && typeof detail === "string" ? detail : null)
      || data?.message
      || `Admin request failed (${r.status})`;
    const err = new Error(msg);
    err.status = r.status;
    err.code = (detail && typeof detail === "object" && detail.code) || null;
    err.raw = data;
    throw err;
  }
  return data;
}

export const adminApi = {
  dashboard: () => req("/dashboard"),
  searchCustomers: (q, limit = 20) => req("/customers/search", { query: { q, limit } }),
  customer: (id) => req(`/customers/${encodeURIComponent(id)}`),
  grantCredits: (payload) =>
    req("/checkin/grant-talk-credits", { method: "POST", body: payload }),
  reconcileAll: (query) =>
    req("/reconcile-square-payments", { method: "POST", query }),
  customerSummaryByEmail: (email) =>
    req("/checkin/customer-summary", { query: { email } }),
  squarePayments: (since_days = 30, limit = 50) =>
    req("/square/payments", { query: { since_days, limit } }),
  reconcileOne: (payment_id) =>
    req(`/square/payments/${encodeURIComponent(payment_id)}/reconcile`, { method: "POST" }),
  entitlements: (query) => req("/entitlements", { query }),
  revokeEntitlement: (id, reason) =>
    req(`/entitlements/${encodeURIComponent(id)}/revoke`, {
      method: "POST", body: { reason },
    }),
  personality: () => req("/personality/components"),
  personalityVersions: () => req("/personality/versions"),
  saveDraft: (components, notes) =>
    req("/personality/draft", { method: "PUT", body: { components, notes } }),
  publishDraft: () => req("/personality/publish", { method: "POST" }),
  rollback: (version) =>
    req(`/personality/rollback/${version}`, { method: "POST" }),
  preview: (payload) => req("/personality/preview", { method: "POST", body: payload }),
  test: (payload) => req("/personality/test", { method: "POST", body: payload }),
  replay: (payload) => req("/personality/replay", { method: "POST", body: payload }),
  explain: (message_id) => req(`/personality/explain/${encodeURIComponent(message_id)}`),
  safetyEvents: (limit = 50) => req("/safety-events", { query: { limit } }),
  settings: () => req("/settings"),
  saveSettings: (section, patch, publish = false) =>
    req("/settings", { method: "PUT", body: { section, patch, publish } }),
  auditLog: (query) => req("/audit-log", { query }),
  webhookEvents: (query) => req("/webhook-events", { query }),
  // Voice Delivery (admin-tunable Dr. C voice settings)
  voiceDelivery: () => req("/voice-delivery"),
  voicePreview: (delivery, sample_key, custom_text) =>
    req("/voice-delivery/preview", {
      method: "POST",
      body: { delivery, sample_key, custom_text: custom_text || null },
    }),
  voiceStatus: () => req("/voice-status"),
  // Conversations (metadata-only + audit-logged reveal)
  conversations: (query) => req("/conversations", { query }),
  revealConversation: (id, reason) =>
    req(`/conversations/${encodeURIComponent(id)}/reveal`,
         { method: "POST", body: { reason } }),
};
